import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Link } from '@ui/components/Link'
import { WarningIcon } from '@ui/icons/Warning.icon'

import { maskEmail } from '@utils/maskEmail'

import { Box } from '@shared/components/Box/Box'
import { VerificationCodeInput } from '@shared/components/VerificationCodeInput/VerificationCodeInput'

enum VerificationErrorCode {
  InvalidCode = 'invalid_verification_code',
  AlreadyVerified = 'lead_verification_error',
}

interface VerifyLeadUsingOTPProps {
  email?: string
  resendLoading?: boolean
  continueLoading?: boolean
  continueLabel?: string
  onResend(event: React.MouseEvent<HTMLElement>): void
  onVerifyCode(code: string): Promise<void>
  onContinue(): Promise<void>
}

export const VerifyLeadUsingOTP: React.FC<VerifyLeadUsingOTPProps> = ({
  email,
  resendLoading = false,
  continueLoading = false,
  continueLabel,
  onResend,
  onVerifyCode,
  onContinue,
}) => {
  const { t } = useTranslation()

  const [code, setCode] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [verificationError, setVerificationError] = useState<string | undefined>(undefined)

  const isComplete = useMemo(() => code.length === 6 && /^\d{6}$/.test(code), [code])

  const resetVerificationState = useCallback(() => {
    setVerificationError(undefined)
  }, [])

  const handleCodeChange = useCallback(
    (nextCode: string) => {
      resetVerificationState()
      setCode(nextCode)
    },
    [resetVerificationState]
  )

  const verifyCode = useCallback(async () => {
    if (!isComplete || isVerifying) return false

    setIsVerifying(true)
    setVerificationError(undefined)

    try {
      await onVerifyCode(code)
      return true
    } catch (error) {
      const errorCode =
        (error as Record<string, unknown>)?.code ??
        (error instanceof Error ? error.message : 'unknown_error')

      if (errorCode === VerificationErrorCode.AlreadyVerified) {
        return true
      }

      if (errorCode === VerificationErrorCode.InvalidCode) {
        setVerificationError(t('Sign up.Incorrect Verification Code'))
      } else {
        setVerificationError(t('Sign up.Something went wrong'))
      }

      return false
    } finally {
      setIsVerifying(false)
    }
  }, [code, isComplete, isVerifying, onVerifyCode, t])

  const handleContinueClick = useCallback(async () => {
    if (continueLoading || isVerifying) return

    if (!isComplete) {
      setVerificationError(t('Sign up.Incorrect verification code'))
      return
    }

    const verified = await verifyCode()

    if (!verified) {
      return
    }

    try {
      await onContinue()
    } catch {
      setVerificationError(t('Sign up.Something went wrong'))
    }
  }, [continueLoading, isVerifying, isComplete, t, verifyCode, onContinue])

  return (
    <Box flex align='center' direction='column' justify='center' gap='x32'>
      <Box flex align='center' direction='column' justify='center' gap='x16'>
        <Heading6 medium color='text-high-em'>
          {t('TwoFactorAuth.Confirm it’s you')}
        </Heading6>

        <Box flex direction='column' align='center'>
          <Body1 color='text-med-em'>
            {email
              ? t('TwoFactorAuth.Enter the verification code sent to')
              : t('Enter the verification code sent to your email')}
          </Body1>
          {!!email && <Body1 color='text-med-em'>{maskEmail(email)}</Body1>}
        </Box>

        <Box flex direction='column' align='center' gap='x4'>
          <VerificationCodeInput
            length={6}
            value={code}
            onChange={handleCodeChange}
            error={!!verificationError}
            disabled={isVerifying || continueLoading}
          />

          {!!verificationError && (
            <Box flex align='center' gap='x6'>
              <WarningIcon size={18} color='text-danger-high-em' />
              <Body1 color='text-danger-high-em'>{verificationError}</Body1>
            </Box>
          )}
        </Box>

        <Box flex gap='x8' align='center' justify='center'>
          <Body1 color='text-med-em' align='center'>
            {t('Sign up.Not get code')}
          </Body1>
          <Link size='lg' disabled={resendLoading} onClick={onResend}>
            {t('Sign up.Resend')}
          </Link>
        </Box>

        <Button.Primary
          size='lg'
          type='button'
          fullWidth
          loading={continueLoading}
          disabled={!isComplete || isVerifying || continueLoading}
          onClick={() => void handleContinueClick()}
        >
          {continueLabel ?? t('Next')}
        </Button.Primary>
      </Box>
    </Box>
  )
}
